import { CommunicationFilter, HasLoggedInFilterType } from 'redux/schemas/models/course-communication';
import { ActivityType } from 'redux/schemas/models/activity';
import { LearnerProgressSavedView } from 'redux/schemas/models/learner-progress-saved-view';
import { LearnerProgressFilters } from './filters-flyout-modal';
import { TabTypes } from './learner-progress-tabs';

export const CONTACT_LEARNERS_DISABLED_TABS: readonly TabTypes[] = [
  TabTypes.IN_PROGRESS,
  TabTypes.MISSED_DEADLINE,
];

/** Contact Learners is available on every view except In Progress / Missed Deadline. */
export const isContactLearnersAvailable = (activeTab: string): boolean => !CONTACT_LEARNERS_DISABLED_TABS.includes(activeTab as TabTypes);

export type LpdViewFilters = {
  inactiveFor: number | null;
  assignmentId: number | null;
  minPoints: number | null;
};

/** LPD filter-panel / saved-view filters -> course-comms CommunicationFilter. */
export const mapLpdFiltersToCommunicationFilter = (f: LpdViewFilters): CommunicationFilter => {
  const filter: CommunicationFilter = {};
  if (f.inactiveFor != null) filter.daysInactive = f.inactiveFor;
  if (f.minPoints != null) filter.earnedLessThanXPoints = f.minPoints;
  if (f.assignmentId != null) {
    filter.hasNotCompleted = { id: f.assignmentId, type: ActivityType.EXERCISE };
  }
  return filter;
};

/** Build the CommunicationFilter for the active LPD view. `undefined` => send to all learners. */
export const buildContactCommunicationFilters = (input: {
  activeTab: string;
  viewFilters?: LpdViewFilters;
  hasUnsavedFilters?: boolean;
}): CommunicationFilter | undefined => {
  // An unsaved filter overrides whatever tab is behind it — activeTab doesn't change
  // when it's applied, so it must be checked before the tab switch below.
  if (input.hasUnsavedFilters) {
    const filter = input.viewFilters ? mapLpdFiltersToCommunicationFilter(input.viewFilters) : {};
    return Object.keys(filter).length ? filter : undefined;
  }
  switch (input.activeTab) {
    case TabTypes.TOTAL:
      return undefined;
    case TabTypes.COMPLETED:
      return { hasCompletedCourse: true };
    case TabTypes.NEVER_ACTIVE:
      return { hasLoggedIn: HasLoggedInFilterType.HAS_NOT_SINCE_RELEASE_DATE };
    case TabTypes.IN_PROGRESS:
    case TabTypes.MISSED_DEADLINE:
      return undefined; // disabled — should not be reached
    default: {
      const filter = input.viewFilters ? mapLpdFiltersToCommunicationFilter(input.viewFilters) : {};
      return Object.keys(filter).length ? filter : undefined;
    }
  }
};

/**
 * Resolves the active view's filters:
 * - unsaved filter panel active  -> from `activeFilters` (respect each *Enabled flag)
 * - saved view active (`saved_<code>`) -> from the matching saved view's `filters`
 * - default tab -> undefined (tab semantics handled by buildContactCommunicationFilters)
 */
export const resolveViewFilters = (input: {
  activeTab: string;
  activeFilters: LearnerProgressFilters | null;
  savedViews: LearnerProgressSavedView[];
}): LpdViewFilters | undefined => {
  if (input.activeFilters) {
    return {
      inactiveFor: input.activeFilters.inactiveForEnabled ? input.activeFilters.inactiveFor : null,
      assignmentId: input.activeFilters.assignmentsEnabled ? input.activeFilters.assignmentId : null,
      minPoints: input.activeFilters.minPointsEnabled ? input.activeFilters.minPoints : null,
    };
  }
  const saved = input.savedViews.find((v) => `saved_${v.viewCode}` === input.activeTab);
  if (saved?.filters) {
    return {
      inactiveFor: saved.filters.inactiveFor ?? null,
      assignmentId: saved.filters.assignmentIds?.[0] ?? null,
      minPoints: saved.filters.minPoints ?? null,
    };
  }
  return undefined;
};
